import { loadFile, loadBinaryFile, loadBinaryLz4File } from "./utils";
import * as THREE from "three";
import * as TOML from "smol-toml";
import * as twgl from "twgl.js";
import { VoxFileParser } from "./vox_file_parser";
import { FakeVoxelScreen } from "./voxel_cmds";
import { VoxelWorld } from "./voxel_ir";

import { TypedSubArray3D, Uint8Array3D } from "./arrays3d";

const vars = TOML.parse(await loadFile("shaders/worldRenderVars.toml"));

const numVoxelsPerTexelDim = Number(vars["NUM_VOXELS_PER_TEXEL_DIM"]);
var numVoxelsPerChunkDim = Number(vars["NUM_VOXELS_PER_CHUNK_DIM"]);
var maxNumChunks = Number(vars["MAX_NUM_CHUNKS"]);

// This is the layout of the chunk buffer
/* V = voxel poisition flags, c = color data
+----+----+----+
| C3 | C6   C7 |
+    +         +
| C2 | C4   C5 |
+----+----+----+
| V  | C0   C1 |
+----+----+----+
*/

const chunkUpdateSize = 32;
var chunkTextureWidth;
var chunkTextureHeight;
var chunkTextureDepth;
var chunkBufferSizeX = 3 * numVoxelsPerChunkDim;
var chunkBufferSizeY = 3 * numVoxelsPerChunkDim;
var chunkBufferSizeZ = 1 * numVoxelsPerChunkDim * maxNumChunks;
var chunkBuffer = new Uint8Array3D(chunkBufferSizeX, chunkBufferSizeY, chunkBufferSizeZ);
console.log(`Chunkbuffer allocated ${chunkBuffer.data.length / 1e6} MB`)

// stores x,y,z of chunk
var chunkOrderBuffer = new Int32Array(6 * maxNumChunks);
var chunkOrderSunBuffer = new Int32Array(6 * maxNumChunks);
var lightsArray = new Array();

function colorVoxelIndexToXYZ(N, index) {
  // Constants
  const d = 2*N*N;
  const Nh = N / 2;
  const width = 3 * Nh;

  const z = Math.floor(index / d);

  index %= d;

  var x, y;
  y = Math.floor(index / N);
  if (y < Nh) {
    x = Nh + index % N;
  } else {
    index -= Nh*N;
    x = index % width;
    y = Math.floor(index/width) + Nh;
  }

  return {x,y,z}
}

function colorVoxelXYZToIndex(N, x, y, z) {
  const d = 2 * N * N;
  const Nh = N / 2;
  const width = 3 * Nh;

  let index;

  if (y < Nh) {
    index = (x - Nh) + N*y;
  } else {
    index = N*Nh;
    index += x + (y - Nh) * width;
  }

  return z * d + index;
}

function voxelIndex(N, x, y, z) {
  return x + N * (y + N * z);
}

function testColorIndex() {
  const a = new Set();
  const b = Array();
  for (var i = 0; i < Math.pow(numVoxelsPerChunkDim, 3); ++i) {
    const { x, y, z } = colorVoxelIndexToXYZ(numVoxelsPerChunkDim, i);
    const ni = colorVoxelXYZToIndex(numVoxelsPerChunkDim, x, y, z);
    if (ni!=undefined && ni != i) {
      console.error("Error in calculating voxel color index");
    }
    a.add(`${x},${y},${z}`);
    b.push({x,y,z})
  }
  if (a.size != b.length) {
    console.error("Error in calculating voxel color index");
  }
}

if(Number(vars["TEST_COLOR_INDEX"]) == 1) {
  testColorIndex();
}

function getVoxelOffset(x, y, z) {
  const localX = x & 1;
  const localY = y & 1;
  const localZ = z & 1;

  return Math.floor(
    localX +
    (localY << 1) +
    (localZ << 2));
}

class Colormap {
  constructor() {
    this.n_components = 4;
    this.default();
  }

  default() {
    this.clear();
    this.generate8BitColormap();
  }

  clear() {
    this.colors = new Float32Array(256 * this.n_components);
  }

  newColor(i, r, g, b, a) {
    this.colors[i * this.n_components] = Math.min(1.0, Math.max(0.0, r));
    this.colors[i * this.n_components + 1] = Math.min(1.0, Math.max(0.0, g));
    this.colors[i * this.n_components + 2] = Math.min(1.0, Math.max(0.0, b));
    if(this.n_components == 4)
      this.colors[i * this.n_components + 3] = Math.min(1.0, Math.max(0.0, a));
  }

  generate8BitColormap() {
    for (let i = 0; i < 256; i++) {
      const t = i / 255; // normalized 0–1

      let r, g, b;

      if (t < 0.25) {
        // blue -> cyan
        const k = t / 0.25;
        r = 0;
        g = k;
        b = 1;
      } else if (t < 0.5) {
        // cyan -> green
        const k = (t - 0.25) / 0.25;
        r = 0;
        g = 1;
        b = 1 - k;
      } else if (t < 0.75) {
        // green -> yellow
        const k = (t - 0.5) / 0.25;
        r = k;
        g = 1;
        b = 0;
      } else {
        // yellow -> red
        const k = (t - 0.75) / 0.25;
        r = 1;
        g = 1 - k;
        b = 0;
      }

      var idx = i * this.n_components;
      this.colors[idx + 0] = r;
      this.colors[idx + 1] = g;
      this.colors[idx + 2] = b;
      if(this.n_components == 4)
        this.colors[idx + 3] = 1.0;
    }
  }

  setColormap(colormap) {
    console.log("TODO")
  }

  makeTexture(gl) {
    var internalFormat = gl.RGB32F;
    var format = gl.RGB;
    if (this.n_components == 4) {
      internalFormat = gl.RGBA32F;
      format = gl.RGBA;
    }
    this.texture = twgl.createTexture(gl, {
      target: gl.TEXTURE_2D,
      width: 256,
      height: 1,
      src: this.colors,
      min: gl.NEAREST,
      mag: gl.NEAREST,
      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,

      internalFormat: internalFormat,
      format: format,
      type: gl.FLOAT,
    });

    return this.texture;
  }
}

class Chunk {
  constructor(index, x,y,z, tx,ty,tz, w,h,d) {
    this.x = x;
    this.y = y;
    this.z = z;
    this.index = index;

    this.tx = tx;
    this.ty = ty;
    this.tz = tz;

    this.buffer = new Uint8Array3D(w, h, d);
  }
}

class VoxelScreen {
  constructor() {
    this.chunks = new Map();
    this.colormap = new Colormap();
    this.indicesModified = new Set();

    this.sunRadius = 15000;
    this.sunTheta = Math.PI * 0.25;
    this.sunPhi   = Math.PI * 0.35;

    this.sunPosition =
      new THREE.Vector3().setFromSpherical(
        new THREE.Spherical(this.sunRadius, this.sunPhi, this.sunTheta)
      );

    // this.fakeScreen = new FakeVoxelScreen(numVoxelsPerChunkDim, maxNumChunks);
    this.fakeScreen = new FakeVoxelScreen(32,10);
  }

  async init() {
    this.voxelIR = await VoxelWorld.create("voxel_ir.wasm", numVoxelsPerChunkDim, maxNumChunks)
  }

  parseCommands() {
    for (const cmd of this.voxelIR.commands()) {
      if (cmd.op == "SET_META") {
        numVoxelsPerChunkDim = cmd.N;
        maxNumChunks = cmd.maxChunks;

        chunkTextureWidth = cmd.chunkTextureWidth;
        chunkTextureHeight = cmd.chunkTextureHeight;
        chunkTextureDepth = cmd.chunkTextureDepth;

        chunkBufferSizeX = cmd.chunkBufferWidth;
        chunkBufferSizeY = cmd.chunkBufferHeight;
        chunkBufferSizeZ = cmd.chunkBufferDepth;

        chunkBuffer = new Uint8Array3D(chunkBufferSizeX, chunkBufferSizeY, chunkBufferSizeZ);

        console.log(`Chunkbuffer allocated ${chunkBuffer.data.length / 1e6} MB`)

        chunkOrderBuffer = new Int32Array(6 * maxNumChunks);
        chunkOrderSunBuffer = new Int32Array(6 * maxNumChunks);

      } else if (cmd.op == "NEW_CHUNK") {
        this.chunks.set(cmd.index, new Chunk(cmd.index, cmd.x, cmd.y, cmd.z, cmd.tx, cmd.ty, cmd.tz, chunkTextureWidth, chunkTextureHeight, chunkTextureDepth));

      } else if (cmd.op == "SET_BIT") {
        chunkBuffer.data[cmd.voxelIndex] |= (1 << cmd.bit);

        const f = Math.floor(cmd.voxelIndex / chunkBufferSizeX) * chunkBufferSizeX;
        this.indicesModified.add(f);

      } else if (cmd.op == "SET_COLOR") {
        chunkBuffer.data[cmd.colorIndex] = cmd.color;

        const f = Math.floor(cmd.colorIndex / chunkBufferSizeX) * chunkBufferSizeX;
        this.indicesModified.add(f);

      } else {
        console.log(cmd);
      }
    }
  }

  setRenderer(renderer) {
    this.renderer = renderer;

    this.colormap.makeTexture(this.renderer.gl);
    this.makeChunkOrderTexture(this.renderer.gl);
    this.makeChunkOrderSunTexture(this.renderer.gl);

    this.parseCommands();

    this.makeChunkTexture(this.renderer.gl);
    this.colormap.makeTexture(this.renderer.gl);
  }

  setCamera(camera) {
    this.camera = camera;
  }

  getChunkKey(x,y,z) {
    return ((z & 0x3FF) << 20) |
           ((y & 0x3FF) << 10) |
           (x & 0x3FF);
  }

  addVoxel(x, y, z, color_index, frame) {
    this.voxelIR.addVoxel(x, y, z, color_index);
  }

  getChunkOrder() {
    const useFreeze = false;

    const ordered = [];

    var position;
    var frustum;
    if (useFreeze) {
      position = this.camera.positionFrozen;
      frustum = this.camera.getFrozenFrustum();
    } else {
      position = this.camera.position;
      frustum = this.camera.getFrustum();
    }

    for (const [i, chunk] of this.chunks.entries()) {
      const box = new THREE.Box3(chunk, new THREE.Vector3(chunk.x + numVoxelsPerChunkDim, chunk.y + numVoxelsPerChunkDim, chunk.z + numVoxelsPerChunkDim));

      if (!frustum.intersectsBox(box)) {
        continue;
      }

      const dist = position.distanceTo(new THREE.Vector3(chunk.x + numVoxelsPerChunkDim / 2, chunk.y + numVoxelsPerChunkDim / 2, chunk.z + numVoxelsPerChunkDim / 2));

      ordered.push({ chunk, dist });
    }

    ordered.sort((a, b) => a.dist - b.dist);

    return ordered.map(o => [
      o.chunk.x,
      o.chunk.y,
      o.chunk.z,
      o.chunk.tx,
      o.chunk.ty,
      o.chunk.tz,
    ]);
  }

  getChunkOrderSun() {
    const ordered = [];

    for (const [i, chunk] of this.chunks.entries()) {
      const dist = this.sunPosition.distanceTo(new THREE.Vector3(chunk.x + numVoxelsPerChunkDim / 2, chunk.y + numVoxelsPerChunkDim / 2, chunk.z + numVoxelsPerChunkDim / 2));

      ordered.push({ chunk, dist });
    }

    ordered.sort((a, b) => a.dist - b.dist);

    return ordered.map(o => [
      o.chunk.x,
      o.chunk.y,
      o.chunk.z,
      o.chunk.tx,
      o.chunk.ty,
      o.chunk.tz,
    ]);
  }

  makeChunkTexture(gl) {
    this.chunkTexture = twgl.createTexture(gl, {
      target: gl.TEXTURE_3D,

      width: chunkBuffer.w,
      height: chunkBuffer.h,
      depth: chunkBuffer.d,

      internalFormat: gl.R8UI,
      format: gl.RED_INTEGER,
      type: gl.UNSIGNED_BYTE,

      src: chunkBuffer.data,

      min: gl.NEAREST,
      mag: gl.NEAREST,

      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
      wrapR: gl.CLAMP_TO_EDGE,
    });
  }

  makeChunkOrderTexture(gl) {
    this.chunkOrderTexture = twgl.createTexture(gl, {
      target: gl.TEXTURE_2D,
      width: 2,
      height: maxNumChunks,

      internalFormat: gl.RGB32I,
      format: gl.RGB_INTEGER,
      type: gl.INT,

      src: chunkOrderBuffer,

      min: gl.NEAREST,
      mag: gl.NEAREST,

      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
    });
  }

  makeChunkOrderSunTexture(gl) {
    this.chunkOrderSunTexture = twgl.createTexture(gl, {
      target: gl.TEXTURE_2D,
      width: 2,
      height: maxNumChunks,

      internalFormat: gl.RGB32I,
      format: gl.RGB_INTEGER,
      type: gl.INT,

      src: chunkOrderSunBuffer,

      min: gl.NEAREST,
      mag: gl.NEAREST,

      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
    });
  }

  makeColorTexture(gl) {
    this.colorTexture = twgl.createTexture(gl, {
      target: gl.TEXTURE_3D,

      width: voxelBufferSize*numVoxelsPerTexelDim,
      height: voxelBufferSize*numVoxelsPerTexelDim,
      depth: voxelBufferSize*numVoxelsPerTexelDim,

      internalFormat: gl.R8UI,
      format: gl.RED_INTEGER,
      type: gl.UNSIGNED_BYTE,

      src: colorBuffer.data,

      min: gl.NEAREST,
      mag: gl.NEAREST,

      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
      wrapR: gl.CLAMP_TO_EDGE,
    });

    return this.chunkTexture;
  }


  setChunkTextureData(gl) {
    gl.bindTexture(gl.TEXTURE_3D, this.chunkTexture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

    for(const index of this.indicesModified) {
      const { x, y, z } = chunkBuffer.coords(index);

      gl.texSubImage3D(
        gl.TEXTURE_3D,
        0,
        x,y,z,
        chunkBufferSizeX, 1, 1,
        gl.RED_INTEGER,
        gl.UNSIGNED_BYTE,
        chunkBuffer.data,
        index
      );
    }

    this.indicesModified = new Set();
  }

  setChunkOrderTextureData(gl) {
    const chunkOrigins = this.getChunkOrder();
    const length = Math.min(chunkOrigins.length + 1, 1024);
    chunkOrigins[length - 1] = [];
    for (let i = 0; i < 8; ++i) {
      chunkOrigins[length - 1].push(-2147483647);
    }

    for (let i = 0; i < length; i++) {
      for (let j = 0; j < 6; j++) {
        chunkOrderBuffer[i*6+j] = chunkOrigins[i][j];
      }
    }

    twgl.setTextureFromArray(gl, this.chunkOrderTexture, chunkOrderBuffer, {
      target: gl.TEXTURE_2D,
      width: 2,
      height: maxNumChunks,

      internalFormat: gl.RGB32I,
      format: gl.RGB_INTEGER,
      type: gl.INT,
    });
  }

  setChunkOrderSunTextureData(gl) {
    const chunkOrigins = this.getChunkOrderSun();
    const length = Math.min(chunkOrigins.length + 1, 1024);
    chunkOrigins[length - 1] = [];
    for (let i = 0; i < 8; ++i) {
      chunkOrigins[length - 1].push(-2147483647);
    }

    for (let i = 0; i < length; i++) {
      for (let j = 0; j < 6; j++) {
        chunkOrderSunBuffer[i*6+j] = chunkOrigins[i][j];
      }
    }

    twgl.setTextureFromArray(gl, this.chunkOrderSunTexture, chunkOrderSunBuffer, {
      target: gl.TEXTURE_2D,
      width: 2,
      height: maxNumChunks,

      internalFormat: gl.RGB32I,
      format: gl.RGB_INTEGER,
      type: gl.INT,
    });
  }

  getUniformsForScreen() {
    const gl = this.renderer.gl;

    if (this.i === undefined) {
      this.i = 0
    }


    this.sunPhi += 0.003;
    this.sunPosition =
      new THREE.Vector3().setFromSpherical(
        new THREE.Spherical(this.sunRadius, this.sunPhi, this.sunTheta)
      );

    // if (this.i < 30) {
    //   for (var x = 0; x < 200; ++x) {
    //     for (var z = 0; z < 200; ++z) {
    //       this.addVoxel(x, this.i, z, 250);
    //     }
    //   }
    //   this.i += 1
    // }


    this.parseCommands();

    this.setChunkTextureData(gl);
    this.setChunkOrderTextureData(gl);
    this.setChunkOrderSunTextureData(gl);

    const uniforms = {
      colormap: this.colormap.texture,
      chunkBuffer: this.chunkTexture,
      chunkOrderBuffer: this.chunkOrderTexture,
      chunkOrderSunBuffer: this.chunkOrderSunTexture,
      sunPosition: this.sunPosition.toArray(),
      lights: lightsArray,
    };

    return uniforms;
  }

  async loadFromFile(filename) {
    const t0 = performance.now();
    const fileBuffer = await loadBinaryLz4File(filename);
    this.parseFile(fileBuffer);
    // this.parseHeader(fileBuffer);
    // this.parseChunks(fileBuffer);
    // this.parseChunkBuffers(fileBuffer);
    // this.parsePalette(fileBuffer);
    // this.parseLights(fileBuffer);
    // this.parseVoxelData(fileBuffer);

    // await Promise.all([
    //   this.parseHeader(fileBuffer),
    //   this.parseChunks(fileBuffer),
    //   this.parseChunkBuffers(fileBuffer),
    //   this.parsePalette(fileBuffer),
    //   this.parseLights(fileBuffer),
    //   this.parseVoxelData(fileBuffer),
    // ]);

      console.log("finished after:", (performance.now() - t0).toFixed(2), "ms");

    this.makeChunkTexture(this.renderer.gl);
    this.colormap.makeTexture(this.renderer.gl);
  }

  parseFile(buffer) {
    const view = new DataView(buffer);

    const magic =
      String.fromCharCode(view.getUint8(0)) +
      String.fromCharCode(view.getUint8(1)) +
      String.fromCharCode(view.getUint8(2)) +
      String.fromCharCode(view.getUint8(3));

    if (magic !== "VBX1") throw new Error("Bad file");

    let base = 4;

    const offsets = [];
    for (let i = 0; i < 5; i++) {
      offsets.push(view.getUint32(base + i * 4, true));
    }

    const fileSize = buffer.byteLength;

    function slice(i) {
      const start = offsets[i];
      const end = (i + 1 < offsets.length) ? offsets[i + 1] : fileSize;
      return new Uint8Array(buffer, start, end - start);
    }

    this.parseHeader(slice(0));
    this.parseChunks(slice(1));
    this.parsePalette(slice(2));
    this.parseLights(slice(3));
    this.parseVoxelData(slice(4));
  }

  parseHeader(u8buffer) {
    const header = new Uint32Array(u8buffer.buffer, u8buffer.byteOffset, u8buffer.byteLength / 4);

    numVoxelsPerChunkDim = header[0]
    maxNumChunks = header[1]

    chunkTextureWidth = header[2];
    chunkTextureHeight = header[3];
    chunkTextureDepth = header[4];

    chunkBufferSizeX = header[5];
    chunkBufferSizeY = header[6];
    chunkBufferSizeZ = header[7];
    chunkBuffer = new Uint8Array3D(chunkBufferSizeX, chunkBufferSizeY, chunkBufferSizeZ);

    console.log(`Chunkbuffer allocated ${chunkBuffer.data.length / 1e6} MB`)

    chunkOrderBuffer = new Int32Array(6 * maxNumChunks);
    chunkOrderSunBuffer = new Int32Array(6 * maxNumChunks);
  }

  parseChunks(u8buffer) {
    var chunkAttrs = new Int32Array(u8buffer.buffer, u8buffer.byteOffset, u8buffer.byteLength / 4);

    for (var i = 0; i < chunkAttrs.length; i += 8) {
      const key = chunkAttrs[i];
      const index = chunkAttrs[i + 1];
      const cx = chunkAttrs[i + 2];
      const cy = chunkAttrs[i + 3];
      const cz = chunkAttrs[i + 4];
      const tx = chunkAttrs[i + 5];
      const ty = chunkAttrs[i + 6];
      const tz = chunkAttrs[i + 7];

      if (!this.chunks.has(index)) {
        this.chunks.set(index, new Chunk(index,cx,cy,cz,tx,ty,tz))
        this.voxelIR.addChunk(cx, cy, cz, tx, ty, tz, index);
      }
    }
  }

  parseChunkBuffers(fileBuffer) {
    // var chunkBuffer_t = this.extractBlock(fileBuffer, "CHUNKBUFFERS");
    // chunkBuffer_t = new Uint8Array(chunkBuffer_t);
    // chunkBuffer.data.set(chunkBuffer_t);
    // console.log(chunkBuffer_t)
  }

  parseVoxelData(u8buffer) {
    const chunkBuffer_t = u8buffer;
    chunkBuffer.data.set(chunkBuffer_t);
  }

  parsePalette(u8buffer) {
    const paletteArray = new Uint32Array(u8buffer.buffer, u8buffer.byteOffset, u8buffer.byteLength / 4);
    const floatArray = Float32Array.from(paletteArray, v => v / 255);
    this.colormap.colors.set(floatArray);
  }

  parseLights(u8buffer) {
    const lightsArray_t = new Int32Array(u8buffer.buffer, u8buffer.byteOffset, u8buffer.byteLength / 4);
    for (let i = 0; i < lightsArray_t.length; ++i) {
      lightsArray.push(lightsArray_t[i]);
    }
  }
}

export { VoxelScreen, Colormap };
